/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import MDBadge from "components/MDBadge";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// Material-UI components
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import apiClient from "./../../../apiClient";
import DataTable from "examples/Tables/DataTable";
import Divider from "@mui/material/Divider";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import MDInput from "components/MDInput";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";

const FetchPoliceData = ({ onRefresh, editData }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [station_name, setStationName] = useState("");
  const [user_id, setUserId] = useState("");
  const [phone, setPhone] = useState("");
  const [city, setCity] = useState("");
  const [statusInput, setStatusInput] = useState("Active");
  const [station_nameFilter, setStationNameFilter] = useState("");
  const [user_idFilter, setUserIdFilter] = useState("");
  const [phoneFilter, setPhoneFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [darkMode, setDarkMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  const PasswordCell = ({ password }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
      <MDBox display="flex" alignItems="center" gap={0.5}>
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {showPassword ? password : "****"}
        </MDTypography>
        <IconButton size="small" onClick={() => setShowPassword((prev) => !prev)}>
          {showPassword ? (
            <VisibilityOffIcon fontSize="inherit" />
          ) : (
            <VisibilityIcon fontSize="inherit" />
          )}
        </IconButton>
      </MDBox>
    );
  };

  PasswordCell.propTypes = {
    password: PropTypes.string,
  };

  PasswordCell.defaultProps = {
    password: "",
  };

  // Fetch states data
  const fetchStates = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/admin/policestation");
      // Sort listings by added date in descending order (most recent first)
      const sortedListings = response.data.sort((a, b) => {
        const dateA = new Date(a.added?.at || a.added_at || a.createdAt || 0);
        const dateB = new Date(b.added?.at || b.added_at || b.createdAt || 0);
        return dateB - dateA;
      });
      setData(sortedListings);
    } catch (error) {
      console.error("Error fetching policestation data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStates();
  }, [onRefresh]);

  // Handlers
  const handleActivate = async (id, status) => {
    try {
      setConfirmDialog({
        open: true,
        title:
          "Are you sure you want to " +
          (status === "Active" ? "deactivate" : "activate") +
          " this policestation?",
        onConfirm: async () => {
          try {
            await apiClient.put(`/admin/policestation/${id}`, { status: status });
            await fetchStates(); // Refresh data while staying on the current page
            // Keep current page after refresh
          } catch (error) {
            console.error("Error updating policestation status:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error activating policestation:", error);
    }
  };

  const handleDelete = async (id, name) => {
    try {
      setConfirmDialog({
        open: true,
        title: `Are you sure you want to delete the policestation "${name}"?`,
        onConfirm: async () => {
          try {
            await apiClient.delete(`/admin/policestation/${id}`);
            fetchStates(); // Refresh data after delete
          } catch (error) {
            console.error("Error deleting policestation:", error);
          }
        },
      });
    } catch (error) {
      console.error("Error deleting policestation:", error);
    }
  };

  const handleEdit = (id) => {
    // Handle edit functionality
    console.log("Edit policestation with ID:", id);
    editData(id);
  };

  const handleStationNameInputChange = (event) => {
    setStationName(event.target.value);
  };

  const handleUserIdInputChange = (event) => {
    setUserId(event.target.value);
  };

  const handlePhoneInputChange = (event) => {
    setPhone(event.target.value);
  };

  const handleCityInputChange = (event) => {
    setCity(event.target.value);
  };

  const handleStatusInputChange = (event) => {
    setStatusInput(event.target.value);
  };

  const handleFilterSubmit = () => {
    setStationNameFilter(station_name);
    setUserIdFilter(user_id);
    setPhoneFilter(phone);
    setCityFilter(city);
    setStatusFilter(statusInput);
  };

  const handleClearFilters = () => {
    setStationNameFilter("");
    setUserIdFilter("");
    setPhoneFilter("");
    setCityFilter("");
    setStatusFilter("");
    setStationName("");
    setUserId("");
    setPhone("");
    setCity("");
  };

  // Filter data
  const filteredRows = data.filter((row) => {
    return (
      (station_nameFilter === "" ||
        row.station_name.toLowerCase().includes(station_nameFilter.toLowerCase())) &&
      (user_idFilter === "" || row.user_id.toLowerCase().includes(user_idFilter.toLowerCase())) &&
      (phoneFilter === "" || row.phone.toLowerCase().includes(phoneFilter.toLowerCase())) &&
      (cityFilter === "" || row.city.toLowerCase().includes(cityFilter.toLowerCase())) &&
      (statusFilter === "" || row.status.toLowerCase().includes(statusFilter.toLowerCase()))
    );
  });

  const columns = [
    { Header: "Station", accessor: "station_name", width: "25%", align: "left" },
    { Header: "User ID", accessor: "user_id", align: "left" },
    { Header: "Password", accessor: "password", align: "left" },
    { Header: "Phone", accessor: "phone", align: "center" },
    { Header: "City", accessor: "city", align: "center" },
    { Header: "Address", accessor: "address", align: "center" },
    { Header: "Added Date", accessor: "added_date", align: "center" },
    { Header: "status", accessor: "status", align: "center" },
    { Header: "Action", accessor: "action", align: "center" },
  ];

  const Author = ({ name, email }) => (
    <MDBox display="flex" textAlign="left" alignItems="left" lineHeight={1}>
      <MDBox ml={2} lineHeight={1}>
        <MDTypography display="block" variant="button" fontWeight="medium">
          {name}
        </MDTypography>
      </MDBox>
    </MDBox>
  );

  const Job = ({ title, description }) => (
    <MDBox lineHeight={1} textAlign="left">
      <MDTypography display="block" variant="caption" color="text" fontWeight="medium">
        {title}
      </MDTypography>
      <MDTypography variant="caption">{description}</MDTypography>
    </MDBox>
  );

  const rows = filteredRows.map((data) => ({
    station_name: <Author name={data.station_name} email={""} />,
    user_id: <Job title={data.user_id} description={data.department} />,
    password: <PasswordCell password={data.password} />,
    phone: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.phone}
      </MDTypography>
    ),
    city: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.city}
      </MDTypography>
    ),
    address: (
      <MDTypography component="a" href="#" variant="caption" color="text" fontWeight="medium">
        {data.address}
      </MDTypography>
    ),
    added_date: new Date(data.added?.at).toLocaleDateString(),
    status: (
      <MDBox ml={-1}>
        <MDBadge
          badgeContent={data.status}
          color={data.status === "Active" ? "success" : "warning"}
          variant="gradient"
          size="sm"
        />
      </MDBox>
    ),
    action: (
      <MDBox display="flex" justifyContent="space-around">
        <IconButton onClick={() => handleEdit(data)} color="secondary">
          <EditIcon />
        </IconButton>
        <IconButton
          onClick={() => handleActivate(data._id, data.status)}
          color={data.status === "Active" ? "warning" : "success"}
        >
          <CheckCircleIcon />
        </IconButton>
        <IconButton onClick={() => handleDelete(data._id, data.station_name)} color="error">
          <DeleteIcon />
        </IconButton>
      </MDBox>
    ),
  }));

  // No data message
  if (loading) return <div>Loading...</div>;
  if (data.length === 0) return <div>No data found.</div>;

  return (
    <>
      <MDBox
        display="flex"
        gap="1rem"
        mb="1rem"
        justifyContent="left"
        alignItems="left"
        ml="2%"
        width="90%"
      >
        <MDInput
          fullWidth
          label="Station Name"
          variant="outlined"
          value={station_name}
          onChange={handleStationNameInputChange}
        />
        <MDInput
          fullWidth
          label="User ID"
          variant="outlined"
          value={user_id}
          onChange={handleUserIdInputChange}
        />
        <MDInput
          fullWidth
          label="Phone"
          variant="outlined"
          value={phone}
          onChange={handlePhoneInputChange}
        />
        <MDInput
          fullWidth
          label="City"
          variant="outlined"
          value={city}
          onChange={handleCityInputChange}
        />
        <Select
          fullWidth
          variant="outlined"
          value={statusInput}
          onChange={handleStatusInputChange}
          displayEmpty
        >
          <MenuItem value="">
            <em>Status</em>
          </MenuItem>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
        <Button
          variant="contained"
          color="primary"
          onClick={handleFilterSubmit}
          sx={{ color: "#fff" }}
        >
          Search
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleClearFilters}
          sx={{ color: "#fff" }}
        >
          Clear
        </Button>
      </MDBox>
      <Divider />
      <DataTable
        table={{ columns, rows }}
        isSorted={false}
        entriesPerPage={false}
        showTotalEntries={false}
        noEndBorder
      />
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        sx={{
          "& .MuiDialog-paper": {
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 1300,
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            color: darkMode ? "#fff" : "#000",
            borderBottom: `1px solid ${darkMode ? "#333" : "#e0e0e0"}`,
          }}
        >
          {confirmDialog.title}
        </DialogTitle>
        <DialogActions
          sx={{
            backgroundColor: darkMode ? "#121212" : "#fff",
            padding: "16px 24px",
          }}
        >
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            color="inherit"
            sx={{ color: darkMode ? "#ccc" : "#666" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              if (confirmDialog.onConfirm) {
                await confirmDialog.onConfirm();
              }
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
            sx={{ color: "#fff", ml: 1 }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
FetchPoliceData.propTypes = {
  onRefresh: PropTypes.func.isRequired,
  editData: PropTypes.func.isRequired,
};

export default FetchPoliceData;
